/**
 * The patient's own link, and the QR that carries it.
 *
 * Five desks may hand it over — Contracts, the front desk, the clinics
 * manager, the secretary and the doctor (SHARE_LINK_ISSUE) — so it is one
 * panel. Issuing a new link revokes the old one server-side, which is what
 * "the patient lost their phone" needs: one press, and the lost link is dead.
 *
 * The page it opens (/p/:token) needs no account and shows the whole hospital
 * record except money and identifiers; see Backend/lib/portalRecord.js.
 */

import { useEffect, useState } from 'react';
import { Badge, Button, ConfirmDialog, ErrorState, Icon, Select, Spinner } from '../../components/common';
import { API_BASE_URL } from '../../api/client';
import { patientsApi } from '../../api/endpoints';
import { usePermissions } from '../../context/PermissionContext';
import { useNotifications } from '../../context/NotificationContext';
import { PERMISSIONS } from '../../domain/rbac';
import { formatDateTime } from './labels';

const TTL = [
  { days: 7, label: 'أسبوع' },
  { days: 30, label: 'شهر' },
  { days: 90, label: 'ثلاثة أشهر' },
];

export function PortalLinkPanel({ patient }) {
  const { can } = usePermissions();
  const { success, error: toastError } = useNotifications();
  const [link, setLink] = useState(undefined);
  const [fresh, setFresh] = useState(null);
  const [ttl, setTtl] = useState(30);
  const [busy, setBusy] = useState(false);
  const [confirmRevoke, setConfirmRevoke] = useState(false);
  const [error, setError] = useState(null);

  const canIssue = can(PERMISSIONS.SHARE_LINK_ISSUE);

  const load = async () => {
    setError(null);
    try {
      const result = await patientsApi.portal(patient.id);
      setLink(result.link);
    } catch (err) {
      setError(err);
    }
  };

  useEffect(() => {
    setFresh(null);
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patient.id]);

  const issue = async () => {
    setBusy(true);
    try {
      const result = await patientsApi.issuePortal(patient.id, ttl);
      setFresh(result);
      setLink({ token: result.token, url: result.url, expiresAt: result.expiresAt, viewCount: 0 });
      success('تم إنشاء رابط المريض');
    } catch (err) {
      toastError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const revoke = async () => {
    setBusy(true);
    try {
      await patientsApi.revokePortal(link.token);
      setLink(null);
      setFresh(null);
      setConfirmRevoke(false);
      success('تم إلغاء الرابط');
    } catch (err) {
      toastError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(link.url);
      success('تم نسخ الرابط');
    } catch {
      toastError('تعذر النسخ — انسخ الرابط يدوياً');
    }
  };

  if (error) return <ErrorState error={error} onRetry={load} />;
  if (link === undefined) return <Spinner label="جارٍ التحقق من الرابط" variant="inline" />;

  const phone = String(patient.phonePrimary || '').replace(/[^0-9]/g, '');
  const whatsapp = fresh?.whatsappUrl || (link && phone ? `https://wa.me/${phone}?text=${encodeURIComponent(link.url)}` : null);
  const qrSrc = link ? `${API_BASE_URL}/public/record/${encodeURIComponent(link.token)}/qr.svg` : null;

  return (
    <div className="space-y-4">
      {link ? (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-[auto_1fr]">
          <div className="mx-auto rounded-2xl bg-white p-3 shadow-lg">
            {fresh?.qrSvg ? (
              <div className="h-40 w-40 [&>svg]:h-full [&>svg]:w-full" dangerouslySetInnerHTML={{ __html: fresh.qrSvg }} />
            ) : (
              <img src={qrSrc} alt="رمز QR لملف المريض" className="h-40 w-40" />
            )}
          </div>
          <div className="min-w-0 space-y-3">
            <p className="break-all rounded-xl bg-white/5 p-3 font-mono text-xs text-cyan-200" dir="ltr">
              {link.url}
            </p>
            <div className="flex flex-wrap gap-2 text-xs">
              <Badge className="border-emerald-500/30 bg-emerald-500/10 text-emerald-200" icon="clock">
                صالح حتى {formatDateTime(link.expiresAt)}
              </Badge>
              <Badge className="border-white/10 bg-white/5 text-slate-300" icon="eye">
                فُتح {link.viewCount || 0} مرة
              </Badge>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button variant="subtle" icon="copy" onClick={copy}>
                نسخ الرابط
              </Button>
              {whatsapp && (
                <a
                  href={whatsapp}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex min-h-[40px] items-center gap-2 rounded-xl bg-emerald-600 px-4 text-sm font-bold text-onaccent hover:bg-emerald-500"
                >
                  <Icon name="send" size={16} /> إرسال واتساب
                </a>
              )}
              <a
                href={link.url}
                target="_blank"
                rel="noreferrer"
                className="inline-flex min-h-[40px] items-center gap-2 rounded-xl bg-white/5 px-3 text-sm font-bold text-slate-300 hover:bg-white/10"
              >
                <Icon name="external-link" size={16} /> فتح الصفحة
              </a>
              {/* The server revokes on SHARE_LINK_ISSUE — whoever may re-issue may kill the old link. */}
              {canIssue && (
                <Button variant="danger" icon="unlink" onClick={() => setConfirmRevoke(true)}>
                  إلغاء الرابط
                </Button>
              )}
            </div>
          </div>
        </div>
      ) : (
        <p className="rounded-xl border border-white/5 bg-white/[0.02] p-4 text-sm text-slate-400">
          لا يوجد رابط فعّال لهذا المريض. أنشئ رابطاً ليتابع ملفه الطبي كاملاً من هاتفه.
        </p>
      )}

      {canIssue && (
        <div className="flex flex-wrap items-end gap-2 border-t border-white/5 pt-4">
          <label className="text-xs text-slate-400">
            مدة الصلاحية
            <Select value={ttl} onChange={(e) => setTtl(Number(e.target.value))} className="mt-1 w-40">
              {TTL.map((t) => (
                <option key={t.days} value={t.days}>
                  {t.label}
                </option>
              ))}
            </Select>
          </label>
          <Button icon="qr-code" loading={busy} onClick={issue}>
            {link ? 'رابط جديد (يلغي القديم)' : 'إنشاء رابط وQR'}
          </Button>
        </div>
      )}

      <ConfirmDialog
        open={confirmRevoke}
        onClose={() => setConfirmRevoke(false)}
        onConfirm={revoke}
        loading={busy}
        destructive
        title="إلغاء رابط المريض"
        message="لن يعمل الرابط الحالي ولا رمز QR المطبوع بعد الإلغاء. هل تريد المتابعة؟"
        confirmLabel="إلغاء الرابط"
      />
    </div>
  );
}

export default PortalLinkPanel;
